import type { ReactNode } from 'react';

import { Brandmark } from '@/components/auth/auth-icons';
import { Button } from '@/components/ui/button';
import { useAuth0 } from '@/context';
import { useSignOut } from '@/hooks/use-sign-out';

/**
 * Full-page shell of the onboarding funnel (ESCLOUD-286): brand header, an
 * optional sticky stepper bar, a centred content column and an optional sticky
 * action footer. The funnel deliberately does not live in the app shell (no
 * sidebar — there is nothing to navigate to yet) nor in the auth split-pane
 * (the card is too small for choice grids and plans).
 *
 * Built on the app's theme tokens, not the design mock's fixed light palette,
 * so dark mode keeps working.
 */
export function OnboardingShell({
    stepper,
    footer,
    children,
}: {
    /** Content of the sticky bar under the header — typically a WizardStepper. */
    stepper?: ReactNode;
    /** Content of the sticky action footer; omitted on screens with no action. */
    footer?: ReactNode;
    children: ReactNode;
}) {
    return (
        <div className="relative flex min-h-screen flex-col bg-background">
            {/* Faint brand glow behind the header, theme-safe. */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-gradient-to-b from-primary/[0.06] to-transparent"
            />

            <header className="relative mx-auto flex w-full max-w-[1004px] items-center justify-between px-8 py-5">
                <div className="flex items-center gap-2.5">
                    <Brandmark className="h-7 w-7 text-primary" />
                    <div className="flex flex-col leading-[1.1]">
                        <span className="text-[17px] font-semibold tracking-[-0.01em] text-foreground">
                            ESCloud
                        </span>
                        <span className="font-mono text-[10px] tracking-[0.12em] text-muted-foreground/80">
                            v{__APP_VERSION__} · CLINICAL
                        </span>
                    </div>
                </div>
                <ShellSignedInAs />
            </header>

            {stepper && (
                <div className="sticky top-0 z-10 border-y border-border bg-background/60 py-3.5 backdrop-blur-md">
                    <div className="mx-auto w-full max-w-[940px] px-8">{stepper}</div>
                </div>
            )}

            <main className="relative mx-auto w-full max-w-[940px] flex-1 px-8 pt-11 pb-14">{children}</main>

            {footer && (
                <footer className="sticky bottom-0 z-10 border-t border-border bg-background/85 backdrop-blur-lg">
                    <div className="mx-auto flex min-h-[68px] w-full max-w-[940px] items-center justify-between gap-5 px-8 py-4">
                        {footer}
                    </div>
                </footer>
            )}
        </div>
    );
}

/** Big step title + one-line subtitle at the top of the content column. */
export function StepTitle({ title, sub }: { title: string; sub: string }) {
    return (
        <div className="mb-9">
            <h1 className="mb-2 text-[32px] font-medium tracking-[-0.028em] text-balance text-foreground">
                {title}
            </h1>
            <p className="m-0 max-w-[560px] text-[15px] leading-[1.55] text-pretty text-muted-foreground">
                {sub}
            </p>
        </div>
    );
}

/** Wrong inbox / wrong account is a real case — the way out is the shared sign-out. */
function ShellSignedInAs() {
    const { user } = useAuth0();
    const signOut = useSignOut();
    return (
        <div className="flex min-w-0 items-center gap-3">
            {user?.email && (
                <span className="hidden truncate font-mono text-[10.5px] tracking-[0.1em] text-muted-foreground/80 uppercase sm:inline">
                    Signed in as {user.email}
                </span>
            )}
            <Button
                type="button"
                variant="link"
                onClick={signOut}
                className="h-auto shrink-0 p-0 text-xs font-medium text-primary"
            >
                Sign out
            </Button>
        </div>
    );
}
